import React, { FC, useContext, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button, { IButtonProps } from 'components/bootstrap/Button';
import OffCanvas, {
	OffCanvasBody,
	OffCanvasHeader,
	OffCanvasTitle,
} from 'components/bootstrap/OffCanvas';
import Alert from 'components/bootstrap/Alert';
import { HeaderRight } from 'layout/Header/Header';
import Icon from 'components/icon/Icon';
import ThemeContext from 'contexts/themeContext';
import useDarkMode from 'hooks/useDarkMode';
import Popovers from 'components/bootstrap/Popovers';
import { useSelector } from 'react-redux';
import { RootState } from 'stores/store';
import { IdTypeEnumForSearchEnum } from 'enums';

interface CommonHeaderRightProps {
	beforeChildren?: any;
	afterChildren: any;
}

const CommonHeaderRight: FC<CommonHeaderRightProps> = ({ beforeChildren, afterChildren }) => {
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();

	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const styledBtn: IButtonProps = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	const [offcanvasStatus, setOffcanvasStatus] = useState(false);

	const { i18n } = useTranslation();

	const { notifications } = useSelector((state: RootState) => state.auth);

	/**
	 * Language attribute
	 */
	useLayoutEffect(() => {
		document.documentElement.setAttribute('lang', i18n.language);
	});

	return (
		<HeaderRight>
			<div className='row g-3'>
				{beforeChildren && beforeChildren}

				{/* Dark Mode */}
				<div className='col-auto'>
					<Popovers trigger='hover' desc='Dark / Light mode'>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							onClick={() => setDarkModeStatus(!darkModeStatus)}
							className='btn-only-icon'
							data-tour='dark-mode'>
							<Icon
								icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
								color={darkModeStatus ? 'info' : 'warning'}
								className='btn-icon'
							/>
						</Button>
					</Popovers>
				</div>

				{/*	Full Screen */}
				<div className='col-auto'>
					<Popovers trigger='hover' desc='Fullscreen'>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							icon={fullScreenStatus ? 'FullscreenExit' : 'Fullscreen'}
							onClick={() => setFullScreenStatus(!fullScreenStatus)}
							aria-label='Toggle dark mode'
							data-tour='full-screen'
						/>
					</Popovers>
				</div>

				{/*	Notifications */}
				<div className='col-auto' data-tour='notification-tour'>
					<Button
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...styledBtn}
						icon='Notifications'
						onClick={() => setOffcanvasStatus(true)}
						aria-label='Notifications'
					/>
				</div>

				{afterChildren}
			</div>

			<OffCanvas
				id='notificationCanvas'
				titleId='offcanvasExampleLabel'
				placement='end'
				isOpen={offcanvasStatus}
				setOpen={setOffcanvasStatus}>
				<OffCanvasHeader setOpen={setOffcanvasStatus}>
					<OffCanvasTitle id='offcanvasExampleLabel'>Notifications</OffCanvasTitle>
				</OffCanvasHeader>
				<OffCanvasBody>
					{notifications &&
						notifications &&
						notifications.map((item: any, i: number) => {
							const namespaceString = item.notifiable_type;
							const namespaceArray = namespaceString.split('\\');
							const lastItem =
								namespaceArray[namespaceArray.length - 1].toLowerCase();
							let icon = 'AccessAlarm';
							switch (lastItem) {
								case IdTypeEnumForSearchEnum.AGENT:
									icon = 'Tonality';
									break;
								case IdTypeEnumForSearchEnum.DISBURSEMENT:
									icon = 'Payments';
									break;
								case IdTypeEnumForSearchEnum.CUSTOMER:
									icon = 'AccountCircle';
									break;
								case IdTypeEnumForSearchEnum.BRANCH:
									icon = 'StackedBarChart';
									break;
								case IdTypeEnumForSearchEnum.LOAN:
									icon = 'AutoAwesomeMotion';
									break;
								case IdTypeEnumForSearchEnum.LOAN_OFFER:
									icon = 'VerticalDistribute';
									break;
								case IdTypeEnumForSearchEnum.PRODUCT:
									icon = 'Terrain';
									break;
								case IdTypeEnumForSearchEnum.USER:
									icon = 'Person';
									break;
								case IdTypeEnumForSearchEnum.SCHEDULE:
									icon = 'AccountBalanceWallet';
									break;
								case IdTypeEnumForSearchEnum.STAFF_LINK:
									icon = 'AccountBalanceWallet';
									break;
							}

							return (
								<Alert
									key={item.id || i}
									icon={icon}
									isLight
									color={item.read_at ? 'light' : 'info'}
									className='flex-nowrap'>
									{item.data.title}
								</Alert>
							);
						})}
				</OffCanvasBody>
			</OffCanvas>
		</HeaderRight>
	);
};

export default CommonHeaderRight;
